import { type ReactNode } from 'react';

import {
  Controller,
  type FieldPath,
  type FieldValues,
  type PathValue,
} from 'react-hook-form';

import { FormFieldError } from '@/components/Form/FormFieldError';
import { FormFieldHelper } from '@/components/Form/FormFieldHelper';
import { FormFieldItem } from '@/components/Form/FormFieldItem';
import { FormFieldLabel } from '@/components/Form/FormFieldLabel';
import { Select, type SelectProps } from '@/components/Select';

import { type FieldCommonProps } from '../FormField.types';

export type FieldMultiSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  type: 'multi-select';
  label?: ReactNode;
  helper?: ReactNode;
  options: Readonly<
    Array<
      Readonly<{
        label: string;
        value: PathValue<TFieldValues, TName>[number];
      }>
    >
  >;
} & Pick<
  SelectProps,
  'size' | 'placeholder' | 'autoFocus' | 'closeMenuOnSelect'
> &
  FieldCommonProps<TFieldValues, TName>;

export const FieldMultiSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FieldMultiSelectProps<TFieldValues, TName>
) => {
  return (
    <Controller
      {...props}
      render={({ field }) => {
        const { value, onChange, ...fieldProps } = field;
        const selectValues =
          props.options?.filter((option) => value?.includes(option.value)) ??
          undefined;
        return (
          <FormFieldItem>
            {!!props.label && <FormFieldLabel>{props.label}</FormFieldLabel>}

            <Select
              type="select"
              isMulti
              closeMenuOnSelect={props.closeMenuOnSelect}
              size={props.size}
              options={props.options}
              placeholder={props.placeholder}
              autoFocus={props.autoFocus}
              value={selectValues}
              onChange={(options) =>
                onChange(options.map((option) => option.value))
              }
              colorScheme="blackAlpha"
              menuPortalTarget={document.body}
              {...fieldProps}
            />

            {!!props.helper && (
              <FormFieldHelper>{props.helper}</FormFieldHelper>
            )}
            <FormFieldError />
          </FormFieldItem>
        );
      }}
    />
  );
};
